/**
 * Kindra OS · Leads tracker  ·  /admin/os/leads
 *
 * Manual pipeline for prospects that don't come through the booking flow.
 * Add · move stages · notes · follow-up date · delete · quick-send.
 *
 * Visual: shared OS dark cockpit (Chrome + C tokens from V2AdminOSPage) —
 * card-per-lead layout, zero horizontal scroll at any width.
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Calendar, PenLine, Info, Trash2, Undo2, Mail, MessageSquare, LayoutGrid, List as ListIcon } from 'lucide-react';
import { Chrome, C } from './V2AdminOSPage';
import V2EmailComposer from './V2EmailComposer';
import { NeonStatus, sendStatus, leadStatus } from './NeonStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = 'kindra-admin-token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY) || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const SURFACE = '#1A1150';
const LINE = '#2A2070';
const TEAL = '#34D1B0';
const MAGENTA = '#EB258D';
const AMBER = '#E4A02A';

// Stage → colour + label.
const STAGES = [
  { key: 'new',             label: 'New',             col: '#4F8FD0' },
  { key: 'contacted',       label: 'Contacted',       col: '#9F6FD0' },
  { key: 'replied',         label: 'Replied',         col: '#F0C36D' },
  { key: 'booked',          label: 'Booked',          col: '#1FB6C1' },
  { key: 'discovery_done',  label: 'Discovery done',  col: '#34D1B0' },
  { key: 'call_done',       label: 'Call done',       col: '#5FE0BE' },
  { key: 'proposal',        label: 'Proposal',        col: '#E4A02A' },
  { key: 'won',             label: 'Won',             col: '#4ADE80' },
  { key: 'lost',            label: 'Lost',            col: '#FF7DB3' },
];
const STAGE_BY_KEY = Object.fromEntries(STAGES.map(s => [s.key, s]));
const SOURCE_LABEL = {
  met_in_person: 'Met in person',
  referral: 'Referral',
  phone_call: 'Phone call',
  linkedin: 'LinkedIn',
  website: 'Website',
  other: 'Other',
};

const StageChip = ({ stage, onClick, testid, small }) => {
  const s = STAGE_BY_KEY[stage] || STAGE_BY_KEY.new;
  return (
    <button data-testid={testid} onClick={onClick}
      style={{
        background: `${s.col}1e`, border: `1px solid ${s.col}66`, color: s.col,
        padding: small ? '2px 8px' : '4px 12px', borderRadius: 999,
        fontFamily: 'Inter', fontWeight: 700, fontSize: small ? 10 : 11,
        letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
      }}>
      {s.label}
    </button>
  );
};

// PORTAL stage menu — the recurring z-index / stacking-context bug is
// fixed once and for all by rendering into document.body at z-index 5000.
// Position is a `fixed` rect computed from the trigger element's
// bounding rect, refreshed on window resize/scroll (which just closes it).
const PortalStageMenu = ({ leadId, rect, currentStage, onPick, onClose }) => {
  const menuRef = useRef(null);
  useEffect(() => {
    const outside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    const scrollOrResize = () => onClose();
    document.addEventListener('mousedown', outside);
    window.addEventListener('resize', scrollOrResize);
    window.addEventListener('scroll', scrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', outside);
      window.removeEventListener('resize', scrollOrResize);
      window.removeEventListener('scroll', scrollOrResize, true);
    };
  }, [onClose]);
  if (!rect) return null;
  // Anchor menu's right edge to trigger's right edge (chips sit on the
  // right of each card); flip up if too close to viewport bottom.
  const vh = window.innerHeight;
  const openUp = rect.bottom + 260 > vh;
  const style = {
    position: 'fixed', zIndex: 5000, minWidth: 170,
    background: '#1A1150', border: '1px solid #2A2070', borderRadius: 10,
    boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: 6,
    top: openUp ? undefined : rect.bottom + 6,
    bottom: openUp ? (vh - rect.top + 6) : undefined,
    left: Math.max(8, Math.min(rect.right - 170, window.innerWidth - 180)),
  };
  return createPortal(
    <div ref={menuRef} data-testid={`stage-menu-${leadId}`} style={style}>
      {STAGES.map(s => (
        <button key={s.key} data-testid={`stage-set-${leadId}-${s.key}`}
          onClick={(e) => { e.stopPropagation(); onPick(s.key); }}
          style={{
            display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px',
            border: 'none', background: currentStage === s.key ? 'rgba(220,240,245,0.06)' : 'transparent',
            color: s.col, fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 6,
            fontFamily: 'Inter',
          }}
          onMouseEnter={(e) => { if (currentStage !== s.key) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
          onMouseLeave={(e) => { if (currentStage !== s.key) e.currentTarget.style.background = 'transparent'; }}>
          {s.label}{currentStage === s.key && <span style={{ float: 'right', fontSize: 10, color: '#5FE0BE' }}>✓</span>}
        </button>
      ))}
    </div>,
    document.body,
  );
};

const btn = (bg, fg = '#fff') => ({
  padding: '8px 16px', borderRadius: 999, border: 'none',
  background: bg, color: fg, fontFamily: 'Inter', fontWeight: 600,
  fontSize: 13, cursor: 'pointer',
});
const ghostBtn = {
  ...btn('transparent', C.muted),
  border: `1px solid ${C.hair}`,
};
const iconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
  padding: '5px 9px', borderRadius: 999, border: `1px solid ${C.hair}`,
  background: 'transparent', color: C.muted, fontFamily: 'Inter',
  fontWeight: 600, fontSize: 11, cursor: 'pointer',
};

const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date().toISOString().slice(0, 10);
  return dateStr <= today;
};

const isCanaryLead = (l) => /^canary\+/i.test(l.email || '') && /@kindraai\.dev$/i.test(l.email || '');

export default function V2AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showSystem, setShowSystem] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: '', business_name: '', email: '', phone: '', source: 'met_in_person', notes: '' });
  const [selected, setSelected] = useState(new Set());
  const [stageMenu, setStageMenu] = useState(null); // { leadId, rect } when open
  const [expanded, setExpanded] = useState(null);   // lead_id when detail open
  const [quickSend, setQuickSend] = useState(null); // {lead, campaigns}
  const [campaigns, setCampaigns] = useState([]);
  const [composeLead, setComposeLead] = useState(null); // lead for composer
  const [composeDraft, setComposeDraft] = useState(null); // AI follow-up draft to preload
  const [queuedDrafts, setQueuedDrafts] = useState([]);
  const [sendHistory, setSendHistory] = useState({});   // lead_id -> sends[]
  const [followups, setFollowups] = useState([]);
  const [replyWatch, setReplyWatch] = useState(null); // {configured, last_poll_at, last_error}
  const [view, setView] = useState('list');              // 'list' | 'board'
  const [showDeleted, setShowDeleted] = useState(false);
  const [tombstones, setTombstones] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const dragLead = React.useRef(null);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 4000); };

  const fetchAll = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('stage', filter);
      if (q) params.set('q', q);
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/os/leads?${params.toString()}`, { headers: authHeaders() }),
        axios.get(`${API}/admin/outreach/campaigns`, { headers: authHeaders() }).catch(() => ({ data: { campaigns: [] } })),
      ]);
      setLeads(r1.data.leads || []);
      setCampaigns(r2.data.campaigns || []);
    } catch (e) {
      showToast('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  useEffect(() => { fetchAll(); }, [filter, q]);
  useEffect(() => {
    axios.get(`${API}/admin/os/leads/followups`, { headers: authHeaders() })
      .then(r => { setFollowups(r.data.leads || []); setQueuedDrafts(r.data.queued_drafts || []); }).catch(() => {});
    axios.get(`${API}/admin/os/reply_watch/status`, { headers: authHeaders() })
      .then(r => setReplyWatch(r.data)).catch(() => {});
  }, []);

  const openFollowupDraft = async (f) => {
    try {
      const r = await axios.get(`${API}/admin/os/leads/${f.lead_id}/followup_draft`, { headers: authHeaders() });
      const lead = leads.find((l) => l.lead_id === f.lead_id)
        || { lead_id: f.lead_id, first_name: f.first_name, business_name: f.business_name, email: f.email };
      setComposeDraft(r.data.draft);
      setComposeLead(lead);
    } catch (e) { showToast('Draft load failed: ' + (e?.response?.data?.detail || e.message)); }
  };


  const loadSends = async (lead_id) => {
    try {
      const r = await axios.get(`${API}/admin/os/leads/${lead_id}/sends`, { headers: authHeaders() });
      setSendHistory(h => ({ ...h, [lead_id]: r.data.sends || [] }));
    } catch { /* non-blocking */ }
  };
  const openDetail = (lead_id) => {
    const next = expanded === lead_id ? null : lead_id;
    setExpanded(next);
    if (next) loadSends(next);
  };

  const markReplied = async (lead) => {
    try {
      const r = await axios.post(`${API}/admin/os/leads/${lead.lead_id}/mark_replied`, {}, { headers: authHeaders() });
      showToast(r.data.stage_after !== r.data.stage_before
        ? `✓ Marked replied — stage → replied` : '✓ Reply noted (stage unchanged)');
      fetchAll();
    } catch (e) { showToast('Mark replied failed'); }
  };

  const addLead = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const body = { ...addForm };
      if (!body.email) delete body.email;
      if (!body.phone) delete body.phone;
      if (!body.notes) delete body.notes;
      await axios.post(`${API}/admin/os/leads`, body, { headers: authHeaders() });
      showToast(`✓ Added ${body.first_name}`);
      setShowAdd(false);
      setAddForm({ first_name: '', business_name: '', email: '', phone: '', source: 'met_in_person', notes: '' });
      fetchAll();
    } catch (e) { showToast('Add failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const changeStage = async (lead, newStage) => {
    setStageMenu(null);
    try {
      await axios.patch(`${API}/admin/os/leads/${lead.lead_id}`, { stage: newStage }, { headers: authHeaders() });
      fetchAll();
    } catch (e) { showToast('Stage change failed'); }
  };

  // ── DELETED-LEADS RESTORE DRAWER ───────────────────────────────────────
  const loadTombstones = async () => {
    setTombstones(null);
    try {
      const r = await axios.get(`${API}/admin/os/leads/tombstones`, { headers: authHeaders() });
      setTombstones(r.data.tombstones || []);
    } catch (e) { showToast('Load failed'); setTombstones([]); }
  };
  const openDeletedDrawer = () => { setShowDeleted(true); loadTombstones(); };
  const restoreTombstone = async (t) => {
    setRestoring(t.lead_id);
    try {
      await axios.post(`${API}/admin/os/leads/${t.lead_id}/restore`, {}, { headers: authHeaders() });
      showToast(`↺ Restored ${t.email || t.lead_id} — will re-appear on the next backfill sweep (or when they land in the source system again).`);
      loadTombstones();
      fetchAll();
    } catch (e) { showToast('Restore failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setRestoring(null); }
  };

  const setFollowUp = async (lead) => {
    const d = prompt(`Set follow-up date for ${lead.first_name} (YYYY-MM-DD, empty to clear):`, lead.follow_up_date || '');
    if (d === null) return;
    try {
      await axios.patch(`${API}/admin/os/leads/${lead.lead_id}`, { follow_up_date: d }, { headers: authHeaders() });
      fetchAll();
    } catch (e) { showToast('Update failed'); }
  };

  const addNote = async (lead) => {
    const t = prompt(`Add a note for ${lead.first_name}:`);
    if (!t) return;
    try {
      await axios.post(`${API}/admin/os/leads/${lead.lead_id}/notes`, { text: t }, { headers: authHeaders() });
      fetchAll();
    } catch (e) { showToast('Add note failed'); }
  };

  const deleteOne = async (lead) => {
    if (!confirm(`Delete lead "${lead.first_name} · ${lead.business_name}"?`)) return;
    try {
      await axios.post(`${API}/admin/os/leads/delete`, { lead_ids: [lead.lead_id], confirm: true }, { headers: authHeaders() });
      fetchAll();
    } catch (e) { showToast('Delete failed'); }
  };

  // Convert a lead → client. Creates/links a `desk_items` client card
  // seeded from the lead's business + email; flips lead stage to `won`;
  // idempotent (calling twice does not create a duplicate). The client
  // then appears on the Desk client cards and in the client-area routes.
  const convertToClient = async (lead) => {
    if (!confirm(`Convert "${lead.first_name || lead.business_name}" from lead to client?\n\nCreates a client record linked to this lead (idempotent — safe to run twice).`)) return;
    try {
      const r = await axios.post(`${API}/admin/os/leads/${lead.lead_id}/convert`,
        {}, { headers: authHeaders() });
      showToast(r.data.created_new_client
        ? '✓ Client created — check the Desk client cards'
        : '✓ Client already linked — refreshed');
      fetchAll();
    } catch (e) { showToast('Convert failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!confirm(`Delete ${selected.size} lead(s)?`)) return;
    await axios.post(`${API}/admin/os/leads/delete`, { lead_ids: [...selected], confirm: true }, { headers: authHeaders() });
    setSelected(new Set());
    fetchAll();
  };

  const startQuickSend = async (lead) => {
    if (!lead.email) { showToast('Lead has no email — edit and add one first.'); return; }
    const active = campaigns[0];
    if (!active) { showToast('No campaigns available.'); return; }
    setQuickSend({ lead, campaign_id: active.campaign_id, campaign_name: active.name });
  };

  // Add the lead's email to a campaign audience. NEVER sends anything —
  // just attaches campaign_id to the underlying outreach_contact. First
  // ensures the contact exists (the leads → outreach bridge). If the
  // contact doesn't exist, we create a placeholder outreach_contact with
  // the lead's email + business so the audience add can succeed.
  const addLeadToCampaign = async (lead, campaign_id) => {
    if (!lead.email) { showToast('Lead has no email.'); return; }
    if (!campaign_id) return;
    try {
      // Ensure the outreach_contact exists for this email. Ignore
      // "already exists" — that's the happy path.
      await axios.post(
        `${API}/admin/outreach/contacts`,
        {
          first_name:    lead.first_name || 'Friend',
          last_name:     lead.last_name || '',
          business_name: lead.business_name || '',
          email:         lead.email,
          phone:         lead.phone || '',
          tag:           'from-lead',
          notes:         `Added from Leads tracker (lead_id=${lead.lead_id})`,
        },
        { headers: authHeaders() },
      ).catch(() => { /* already exists — fine */ });
      const r = await axios.post(
        `${API}/admin/outreach/campaigns/${campaign_id}/audience/add`,
        { emails: [lead.email] },
        { headers: authHeaders() },
      );
      const c = campaigns.find(x => x.campaign_id === campaign_id);
      showToast(`✓ Added ${lead.first_name} to “${c?.name || campaign_id}” (no email sent — ${r.data.added} row)`);
    } catch (e) {
      showToast('Add to campaign failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const confirmQuickSend = async (overrideCap = false) => {
    if (!quickSend) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/os/leads/${quickSend.lead.lead_id}/quick_send`,
        { campaign_id: quickSend.campaign_id, confirm: true, ...(overrideCap ? { override_cap: true } : {}) },
        { headers: authHeaders() },
      );
      const modeLabel = r.data.test_mode ? ' (test mode → michael@kindraai.dev)' : '';
      const stageMsg = r.data.stage_after !== r.data.stage_before ? ` · stage → ${r.data.stage_after}` : '';
      showToast(`✓ Sent to ${quickSend.lead.first_name}${modeLabel}${stageMsg}`);
      setQuickSend(null);
      fetchAll();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === 'daily_cap_reached') {
        const go = window.confirm(
          `Warm-up cap reached: ${det.sent_today}/${det.cap} sends today (week ${det.week} of domain warm-up).\n\nExceeding it can hurt deliverability on a new domain. Send anyway?`);
        if (go) { setBusy(false); await confirmQuickSend(true); return; }
        showToast('Held — daily warm-up cap respected');
      } else {
        showToast('Quick send failed: ' + (typeof det === 'string' ? det : e.message));
      }
    }
    finally { setBusy(false); }
  };

  const undoAdvance = async (lead) => {
    try {
      await axios.post(`${API}/admin/os/leads/${lead.lead_id}/undo_stage_advance`, {}, { headers: authHeaders() });
      showToast('Stage undo done');
      fetchAll();
    } catch (e) { showToast('Undo failed'); }
  };

  const visibleLeads = useMemo(
    () => (showSystem ? leads : leads.filter((l) => !isCanaryLead(l))),
    [leads, showSystem],
  );
  const hiddenCount = leads.length - visibleLeads.length;

  const sortedLeads = useMemo(() => {
    // Sort: overdue follow-ups first, then most-recently-updated.
    const arr = [...visibleLeads];
    arr.sort((a, b) => {
      const aov = isOverdue(a.follow_up_date) ? 1 : 0;
      const bov = isOverdue(b.follow_up_date) ? 1 : 0;
      if (aov !== bov) return bov - aov;
      return (b.updated_at || '').localeCompare(a.updated_at || '');
    });
    return arr;
  }, [visibleLeads]);

  const totalByStage = useMemo(() => {
    const m = {};
    for (const l of visibleLeads) m[l.stage] = (m[l.stage] || 0) + 1;
    return m;
  }, [visibleLeads]);

  return (
    <Chrome activeTab="leads">
      <div data-testid="leads-page">
        {/* Page heading */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: C.faint }}>
            Kindra OS · Leads
          </p>
          <h1 style={{ fontFamily: 'Poppins,Inter,sans-serif', fontSize: 'clamp(24px,3vw,30px)', fontWeight: 600, marginTop: 4, color: C.icy }}>
            Leads
          </h1>
          <p style={{ fontFamily: 'Inter', fontSize: 13, color: C.muted, marginTop: 6, maxWidth: 640 }}>
            People who&apos;ve reached out — add them to Hunting Grounds when you&apos;re ready to email them.
          </p>
          {replyWatch && (
            <span data-testid="reply-watch-status"
                  title={replyWatch.configured
                    ? `Read-only Outlook watch (Mail.Read) on ${replyWatch.mailbox}. Last check: ${replyWatch.last_poll_at ? replyWatch.last_poll_at.slice(0, 16).replace('T', ' ') : 'pending'}${replyWatch.last_error ? ` · error: ${replyWatch.last_error}` : ''}. Replies auto-mark leads as Replied — manual button always works too.`
                    : 'Not connected yet — waiting on Microsoft Graph credentials. Manual "Replied" button works as always.'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
                           padding: '4px 12px', borderRadius: 999, fontFamily: 'Inter', fontSize: 11, fontWeight: 600,
                           border: `1px solid ${replyWatch.configured && !replyWatch.last_error ? `${TEAL}66` : C.hair}`,
                           color: replyWatch.configured && !replyWatch.last_error ? TEAL : C.faint }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%',
                             background: replyWatch.configured ? (replyWatch.last_error ? AMBER : TEAL) : C.faint }} />
              Auto reply-watch: {replyWatch.configured ? (replyWatch.last_error ? 'error — manual fallback active' : 'active') : 'not connected (manual fallback)'}
              {replyWatch.configured && replyWatch.total_matched > 0 && ` · ${replyWatch.total_matched} detected`}
            </span>
          )}
        </div>

        {/* Follow-up digest — read-only reminder, never auto-sends */}
        {(followups.length > 0 || queuedDrafts.length > 0) && (
          <section data-testid="leads-followup-digest"
                   style={{ background: 'rgba(228,160,42,0.10)', border: `1px solid ${AMBER}66`, borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
            {followups.length > 0 && (
              <p style={{ fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700, color: AMBER, margin: 0 }}>
                ⚠ {followups.length} lead{followups.length > 1 ? 's' : ''} need{followups.length > 1 ? '' : 's'} follow-up
              </p>
            )}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {followups.map((f) => (
                <span key={f.lead_id} style={{ display: 'inline-flex', gap: 4 }}>
                  <button data-testid={`followup-chip-${f.lead_id}`}
                          onClick={() => { setQ(f.first_name || ''); openDetail(f.lead_id); }}
                          style={{ background: 'transparent', border: `1px solid ${AMBER}55`, borderRadius: 999, color: C.icy, fontFamily: 'Inter', fontSize: 11.5, padding: '4px 12px', cursor: 'pointer' }}>
                    {f.first_name}{f.business_name ? ` · ${f.business_name}` : ''} <span style={{ color: AMBER }}>({f.follow_up_date})</span>
                  </button>
                  {f.has_draft && (
                    <button data-testid={`followup-draft-${f.lead_id}`} onClick={() => openFollowupDraft(f)}
                            title={`AI draft ready: "${f.draft_subject || ''}" — opens in the studio, sends only through the normal gate`}
                            style={{ background: 'rgba(31,182,193,0.12)', border: `1px solid ${TEAL}66`, borderRadius: 999, color: TEAL, fontFamily: 'Inter', fontSize: 11.5, fontWeight: 700, padding: '4px 12px', cursor: 'pointer' }}>
                      ✦ Open draft
                    </button>
                  )}
                </span>
              ))}
            </div>
            {queuedDrafts.length > 0 && (
              <div data-testid="queued-drafts-row" style={{ marginTop: followups.length ? 10 : 0 }}>
                <p style={{ fontFamily: 'Inter', fontSize: 11.5, fontWeight: 700, color: TEAL, margin: '0 0 6px' }}>
                  ✦ {queuedDrafts.length} AI follow-up draft{queuedDrafts.length > 1 ? 's' : ''} queued (review any time — never auto-sends)
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {queuedDrafts.map((d) => (
                    <button key={d.draft_id} data-testid={`queued-draft-${d.draft_id}`} onClick={() => openFollowupDraft(d)}
                            style={{ background: 'transparent', border: `1px solid ${TEAL}55`, borderRadius: 999, color: C.icy, fontFamily: 'Inter', fontSize: 11.5, padding: '4px 12px', cursor: 'pointer' }}>
                      {d.first_name}{d.business_name ? ` · ${d.business_name}` : ''} <span style={{ color: TEAL }}>(suggested {d.suggested_date})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Filter row */}
        <section style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <button data-testid="filter-all" onClick={() => setFilter('all')}
            style={{ ...btn(filter === 'all' ? C.icy : 'transparent', filter === 'all' ? C.navy : C.icy), border: `1px solid ${filter === 'all' ? C.icy : C.hairBright}` }}>
            All ({leads.length})
          </button>
          {STAGES.map(s => (
            <button key={s.key} data-testid={`filter-${s.key}`} onClick={() => setFilter(s.key)}
              style={{ padding: '6px 12px', borderRadius: 999, border: `1px solid ${s.col}${filter === s.key ? '' : '66'}`,
                background: filter === s.key ? s.col : `${s.col}14`,
                color: filter === s.key ? C.navy : s.col,
                fontFamily: 'Inter', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              {s.label} {totalByStage[s.key] ? `(${totalByStage[s.key]})` : ''}
            </button>
          ))}
          <div data-testid="leads-view-toggle" style={{ marginLeft: 'auto', display: 'inline-flex', border: `1px solid ${LINE}`, borderRadius: 999, overflow: 'hidden' }}>
            {['list', 'board'].map(v => (
              <button key={v} data-testid={`leads-view-${v}`} onClick={() => setView(v)}
                style={{ padding: '7px 14px', border: 'none', cursor: 'pointer', fontFamily: 'Inter', fontWeight: 700, fontSize: 11,
                         letterSpacing: '0.06em', textTransform: 'uppercase',
                         background: view === v ? TEAL : 'transparent', color: view === v ? C.navy : C.muted }}>
                {v === 'list' ? 'List' : 'Board'}
              </button>
            ))}
          </div>
          <input placeholder="search name/biz/email/phone" value={q} onChange={e => setQ(e.target.value)} data-testid="leads-search"
            style={{ padding: '8px 12px', borderRadius: 999, border: `1px solid ${LINE}`,
                     background: SURFACE, color: C.icy, fontSize: 13, width: 220, fontFamily: 'Inter', outline: 'none' }} />
          {(hiddenCount > 0 || showSystem) && (
            <button data-testid="leads-show-system-toggle" onClick={() => setShowSystem(!showSystem)}
              style={{ ...btn('transparent', C.faint), border: `1px dashed ${C.hair}`, fontSize: 11 }}>
              {showSystem ? 'Hide system rows' : `Show system rows (${hiddenCount})`}
            </button>
          )}
          {selected.size > 0 && (
            <button data-testid="leads-delete-selected" onClick={deleteSelected} style={btn(MAGENTA)}>
              Delete {selected.size} selected
            </button>
          )}
          <button data-testid="add-lead-btn" onClick={() => setShowAdd(true)} style={{ ...btn(C.spectrum, '#fff'), fontWeight: 700 }}>+ Add lead</button>
          <button data-testid="leads-open-deleted-drawer" onClick={openDeletedDrawer}
            title="Show deleted leads · restorable"
            style={{ ...ghostBtn, fontSize: 12, fontFamily: 'Inter', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Undo2 size={13} /> Deleted
          </button>
        </section>

        {/* Leads — one card per lead, zero horizontal scroll at any width */}
        {view === 'list' && (
        <section data-testid="leads-cards" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sortedLeads.map(l => {
            const overdue = isOverdue(l.follow_up_date);
            const lastNote = (l.notes_log || []).slice(-1)[0];
            return (
              <div key={l.lead_id} data-testid={`lead-row-${l.lead_id}`}
                   onClick={(e) => {
                     if (e.target.closest('button,select,input,a')) return;
                     openDetail(l.lead_id);
                   }}
                   title="Click to open lead details"
                   style={{ background: overdue ? 'rgba(228,160,42,0.10)' : SURFACE,
                            border: `1px solid ${overdue ? `${AMBER}66` : LINE}`, borderRadius: 12,
                            padding: '13px 16px', cursor: 'pointer' }}>
                {/* Top row — identity left, stage chip right */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                  <input type="checkbox" data-testid={`lead-select-${l.lead_id}`}
                    style={{ marginTop: 4, accentColor: TEAL }}
                    checked={selected.has(l.lead_id)}
                    onChange={() => { const s = new Set(selected); s.has(l.lead_id) ? s.delete(l.lead_id) : s.add(l.lead_id); setSelected(s); }} />
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <span style={{ fontWeight: 700, color: C.icy, fontSize: 14, fontFamily: 'Inter' }}>{l.first_name}</span>
                    {l.business_name && <span style={{ fontWeight: 700, color: C.icy, fontSize: 14, fontFamily: 'Inter' }}> · {l.business_name}</span>}
                    {(l.linked_booking_id || l.linked_session_id) && (
                      <span title="Auto-linked from booking/session" style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', borderRadius: 4, background: `${TEAL}22`, color: '#5FE0BE', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        AUTO-LINKED
                      </span>
                    )}
                  </div>
                  <NeonStatus state={leadStatus(l)} testId={`lead-neon-${l.lead_id}`} />
                  <div style={{ position: 'relative', flex: '0 0 auto' }}>
                    <StageChip stage={l.stage} testid={`stage-chip-${l.lead_id}`}
                      onClick={(e) => {
                        if (stageMenu?.leadId === l.lead_id) { setStageMenu(null); return; }
                        const rect = e.currentTarget.getBoundingClientRect();
                        setStageMenu({ leadId: l.lead_id, rect });
                      }} />
                  </div>
                </div>
                {/* Contact line */}
                <p style={{ margin: '7px 0 0', fontSize: 12, color: C.muted, overflowWrap: 'anywhere', fontFamily: 'Inter' }}>
                  <span style={{ fontFamily: 'monospace' }}>{l.email || '—'}</span>
                  {' · '}{l.phone || '—'}
                  {' · '}{SOURCE_LABEL[l.source] || l.source || '—'}
                </p>
                {/* Follow-up + latest note */}
                {(l.follow_up_date || lastNote) && (
                  <p style={{ margin: '5px 0 0', fontSize: 12, display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0, fontFamily: 'Inter' }}>
                    {l.follow_up_date && (
                      <span style={{ color: overdue ? AMBER : C.muted, fontWeight: overdue ? 700 : 500, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {overdue && '⚠ '}<Calendar size={12} style={{ display: 'inline' }} /> {l.follow_up_date}
                      </span>
                    )}
                    {lastNote && (
                      <span style={{ color: C.faint, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lastNote.text}
                      </span>
                    )}
                  </p>
                )}
                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {campaigns.length > 0 && (
                    <select
                      data-testid={`lead-add-to-campaign-${l.lead_id}`}
                      disabled={!l.email || busy}
                      onChange={(e) => { const cid = e.target.value; if (cid) { addLeadToCampaign(l, cid); e.target.value = ''; } }}
                      title={l.email ? 'Add this lead to a campaign audience (no email sent)' : 'Lead has no email'}
                      style={{ padding: '4px 8px', fontSize: 11, borderRadius: 999, border: `1px solid ${LINE}`, background: C.navy, color: C.icy, opacity: l.email ? 1 : 0.4, maxWidth: 150, fontFamily: 'Inter' }}>
                      <option value="">＋ Add to campaign…</option>
                      {campaigns.map((c) => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
                    </select>
                  )}
                  <button disabled={!l.email || busy} onClick={() => startQuickSend(l)} data-testid={`lead-quicksend-${l.lead_id}`}
                    title={l.email ? 'Quick-send an intro email' : 'Lead has no email'}
                    style={{ ...btn(TEAL, C.navy), padding: '4px 11px', fontSize: 11, fontWeight: 700, opacity: l.email ? 1 : 0.4 }}>✉ Quick send</button>
                  <button disabled={!l.email || busy} onClick={() => setComposeLead(l)} data-testid={`lead-compose-${l.lead_id}`}
                    title={l.email ? 'Compose a branded email to this lead' : 'Lead has no email'}
                    style={{ ...btn('transparent', TEAL), border: `1px solid ${TEAL}66`, padding: '4px 11px', fontSize: 11, fontWeight: 700, opacity: l.email ? 1 : 0.4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Mail size={12} /> Compose</button>
                  <button onClick={() => markReplied(l)} data-testid={`lead-mark-replied-${l.lead_id}`}
                    title="They replied — log it and move stage to Replied"
                    style={{ ...iconBtn, color: '#F0C36D', border: '1px solid rgba(240,195,109,0.4)' }}>
                    <MessageSquare size={13} /> Replied</button>
                  <button onClick={() => setFollowUp(l)} data-testid={`lead-followup-${l.lead_id}`}
                    title="Set follow-up date" aria-label="Set follow-up date" style={iconBtn}><Calendar size={13} /></button>
                  <button onClick={() => addNote(l)} data-testid={`lead-note-${l.lead_id}`}
                    title="Add a note" aria-label="Add a note" style={iconBtn}><PenLine size={13} /></button>
                  <button onClick={() => openDetail(l.lead_id)} data-testid={`lead-expand-${l.lead_id}`}
                    title="Open details" aria-label="Open details" style={iconBtn}><Info size={13} /></button>
                  {l.stage !== 'won' && (
                    <button onClick={() => convertToClient(l)} data-testid={`lead-convert-${l.lead_id}`}
                      title="Convert to client (creates a client card on the Desk)"
                      style={{ ...iconBtn, color: TEAL, border: `1px solid ${TEAL}55`, fontWeight: 700 }}>
                      → Client
                    </button>
                  )}
                  <button onClick={() => deleteOne(l)} data-testid={`lead-delete-${l.lead_id}`}
                    title="Delete lead" style={{ ...iconBtn, color: '#FF7DB3', border: `1px solid ${MAGENTA}55` }}><Trash2 size={13} /> Delete</button>
                  {l.stage_prev_before_quicksend && (
                    <button onClick={() => undoAdvance(l)} data-testid={`lead-undo-${l.lead_id}`}
                      style={{ ...iconBtn, color: AMBER, border: `1px solid ${AMBER}55` }}
                      title={`Undo advance to ${l.stage}`}><Undo2 size={13} /> Undo</button>
                  )}
                </div>
                {/* Expanded timeline */}
                {expanded === l.lead_id && (
                  <div data-testid={`lead-detail-${l.lead_id}`}
                       style={{ marginTop: 12, padding: 14, background: 'rgba(220,240,245,0.04)', borderRadius: 10, border: `1px solid ${LINE}` }}>
                    <p style={{ fontSize: 12, color: C.faint, margin: '0 0 8px', overflowWrap: 'anywhere', fontFamily: 'Inter' }}>
                      Created {l.created_at?.slice(0, 16).replace('T', ' ')} · Updated {l.updated_at?.slice(0, 16).replace('T', ' ')}
                      {l.email && <> · <span style={{ fontFamily: 'monospace' }}>{l.email}</span></>}
                      {l.phone && <> · {l.phone}</>}
                      {l.source && <> · Source: {SOURCE_LABEL[l.source] || l.source}</>}
                      {l.linked_booking_id && <> · Booking <code>{l.linked_booking_id.slice(0, 12)}</code></>}
                      {l.linked_session_id && <> · Session <code>{l.linked_session_id.slice(0, 12)}</code></>}
                    </p>
                    <p style={{ fontFamily: 'Poppins,Inter,sans-serif', fontSize: 13, fontWeight: 700, margin: '4px 0 6px', color: C.icy }}>Timeline</p>
                    {(l.notes_log || []).length === 0
                      ? <p style={{ color: C.faint, fontSize: 12, fontFamily: 'Inter' }}>No notes yet.</p>
                      : (l.notes_log || []).slice(-10).reverse().map((n, i) => (
                          <p key={i} style={{ fontSize: 12, color: C.muted, margin: '3px 0', overflowWrap: 'anywhere', fontFamily: 'Inter' }}>
                            <span style={{ color: C.faint }}>{n.ts?.slice(0, 16).replace('T', ' ')} · {n.kind}</span> — {n.text}
                          </p>
                        ))}
                    <p style={{ fontFamily: 'Poppins,Inter,sans-serif', fontSize: 13, fontWeight: 700, margin: '12px 0 6px', color: C.icy }}>Send history</p>
                    <div data-testid={`lead-send-history-${l.lead_id}`}>
                      {(sendHistory[l.lead_id] || []).length === 0
                        ? <p style={{ color: C.faint, fontSize: 12, fontFamily: 'Inter' }}>No outreach sends yet.</p>
                        : sendHistory[l.lead_id].map((s) => {
                            return (
                            <p key={s.send_id} style={{ fontSize: 12, color: C.muted, margin: '5px 0', overflowWrap: 'anywhere', fontFamily: 'Inter' }}
                               title="Delivered = accepted by their mail server. Inbox vs junk isn't measurable.">
                              <NeonStatus state={sendStatus(s)} pinned testId={`send-status-${s.send_id}`} />
                              {' — '}{s.subject || s.campaign_id}
                              <span style={{ color: C.faint }}>
                                {' · sent '}{(s.sent_at || s.queued_at || '').slice(0, 16).replace('T', ' ')}
                                {s.opened_at ? ` · opened ${s.opened_at.slice(0, 16).replace('T', ' ')}` : ''}
                                {s.replied_at ? ` · replied ${s.replied_at.slice(0, 16).replace('T', ' ')}` : ''}
                              </span>
                            </p>
                            );
                          })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {sortedLeads.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: C.faint, background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 12, fontFamily: 'Inter' }}>
              No leads yet — click <strong>+ Add lead</strong> to get started.
            </div>
          )}
        </section>
        )}

        {/* KANBAN BOARD — drag a card between columns to change stage */}
        {view === 'board' && (
        <section data-testid="leads-board" style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16, alignItems: 'flex-start' }}>
          {STAGES.map(s => {
            const colLeads = sortedLeads.filter(l => (l.stage || 'new') === s.key);
            return (
              <div key={s.key} data-testid={`board-col-${s.key}`}
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={(e) => {
                     e.preventDefault();
                     const lead = dragLead.current;
                     dragLead.current = null;
                     if (lead && lead.stage !== s.key) changeStage(lead, s.key);
                   }}
                   style={{ flex: '0 0 230px', background: 'rgba(26,17,80,0.55)', border: `1px solid ${LINE}`,
                            borderTop: `2px solid ${s.col}`, borderRadius: 12, padding: 10, minHeight: 120 }}>
                <p style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: s.col, margin: '2px 4px 10px' }}>
                  {s.label} <span style={{ color: C.faint }}>({colLeads.length})</span>
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {colLeads.map(l => (
                    <div key={l.lead_id} data-testid={`board-card-${l.lead_id}`}
                         draggable
                         onDragStart={() => { dragLead.current = l; }}
                         onClick={() => { setView('list'); setFilter('all'); openDetail(l.lead_id); }}
                         title="Drag to another column to change stage · click to open in list"
                         style={{ background: SURFACE, border: `1px solid ${isOverdue(l.follow_up_date) ? `${AMBER}66` : LINE}`,
                                  borderRadius: 10, padding: '9px 11px', cursor: 'grab' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <p style={{ fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700, color: C.icy, margin: 0, flex: 1, minWidth: 0 }}>
                          {l.first_name}{l.business_name ? ` · ${l.business_name}` : ''}
                        </p>
                        <NeonStatus state={leadStatus(l)} pinned={leadStatus(l) === 'replied'} testId={`board-neon-${l.lead_id}`} />
                      </div>
                      <p style={{ fontFamily: 'Inter', fontSize: 11, color: C.muted, margin: '3px 0 0' }}>
                        {SOURCE_LABEL[l.source] || l.source || ''}
                        {l.follow_up_date && <span style={{ color: isOverdue(l.follow_up_date) ? AMBER : C.faint }}> · ↻ {l.follow_up_date}</span>}
                      </p>
                    </div>
                  ))}
                  {colLeads.length === 0 && (
                    <div style={{ padding: '14px 6px', textAlign: 'center', color: C.faint, fontFamily: 'Inter', fontSize: 11, border: `1px dashed ${LINE}`, borderRadius: 8 }}>
                      drop here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </section>
        )}
      </div>

      {/* COMPOSE-TO-LEAD — the full drag-drop composer, prefilled */}
      {composeLead && (
        <V2EmailComposer
          leadContext={composeLead}
          initialDraft={composeDraft}
          campaigns={campaigns}
          showToast={showToast}
          onClose={() => { setComposeLead(null); setComposeDraft(null); fetchAll(); }}
        />
      )}

      {/* ADD MODAL */}
      {showAdd && (
        <div data-testid="add-lead-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(16,8,56,0.78)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}>
          <form onSubmit={addLead} style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 16, padding: 28, maxWidth: 520, width: '100%' }}>
            <h2 style={{ fontFamily: 'Poppins,Inter,sans-serif', fontSize: 20, marginTop: 0, marginBottom: 4, color: C.icy }}>Add a lead</h2>
            <p style={{ fontSize: 12, color: C.faint, marginBottom: 18, fontFamily: 'Inter' }}>First name and business are required. Email optional — sometimes you only get a name and a number.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ fontSize: 11, color: C.muted, gridColumn: '1 / 2', fontFamily: 'Inter' }}>First name *
                <input required value={addForm.first_name} onChange={e => setAddForm({ ...addForm, first_name: e.target.value })} data-testid="add-lead-first-name"
                  style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${LINE}`, background: C.navy, color: C.icy, marginTop: 4, fontSize: 14, fontFamily: 'Inter' }} />
              </label>
              <label style={{ fontSize: 11, color: C.muted, fontFamily: 'Inter' }}>Business *
                <input required value={addForm.business_name} onChange={e => setAddForm({ ...addForm, business_name: e.target.value })} data-testid="add-lead-business"
                  style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${LINE}`, background: C.navy, color: C.icy, marginTop: 4, fontSize: 14, fontFamily: 'Inter' }} />
              </label>
              <label style={{ fontSize: 11, color: C.muted, fontFamily: 'Inter' }}>Email
                <input type="email" value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} data-testid="add-lead-email"
                  style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${LINE}`, background: C.navy, color: C.icy, marginTop: 4, fontSize: 14, fontFamily: 'Inter' }} />
              </label>
              <label style={{ fontSize: 11, color: C.muted, fontFamily: 'Inter' }}>Phone
                <input value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value })} data-testid="add-lead-phone"
                  style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${LINE}`, background: C.navy, color: C.icy, marginTop: 4, fontSize: 14, fontFamily: 'Inter' }} />
              </label>
              <label style={{ fontSize: 11, color: C.muted, gridColumn: '1 / -1', fontFamily: 'Inter' }}>Source
                <select value={addForm.source} onChange={e => setAddForm({ ...addForm, source: e.target.value })} data-testid="add-lead-source"
                  style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${LINE}`, background: C.navy, color: C.icy, marginTop: 4, fontSize: 14, fontFamily: 'Inter' }}>
                  {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 11, color: C.muted, gridColumn: '1 / -1', fontFamily: 'Inter' }}>Notes
                <textarea value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} data-testid="add-lead-notes"
                  rows={3} style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${LINE}`, background: C.navy, color: C.icy, marginTop: 4, fontSize: 14, resize: 'vertical', fontFamily: 'Inter' }} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button type="button" onClick={() => setShowAdd(false)} style={ghostBtn}>Cancel</button>
              <button type="submit" disabled={busy} data-testid="add-lead-submit" style={{ ...btn(C.spectrum, '#fff'), fontWeight: 700 }}>{busy ? '…' : 'Add lead'}</button>
            </div>
          </form>
        </div>
      )}

      {/* QUICK SEND MODAL */}
      {quickSend && (
        <div data-testid="lead-quicksend-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(16,8,56,0.82)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}>
          <div style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 16, padding: 28, maxWidth: 480, width: '100%' }}>
            <p style={{ fontSize: 11, letterSpacing: '.20em', textTransform: 'uppercase', color: TEAL, fontWeight: 700, fontFamily: 'Inter' }}>QUICK SEND · single lead · click-is-the-gate</p>
            <h2 style={{ fontFamily: 'Poppins,Inter,sans-serif', fontSize: 20, margin: '12px 0 8px', color: C.icy }}>
              Send &ldquo;{quickSend.campaign_name}&rdquo; to {quickSend.lead.first_name}?
            </h2>
            <div style={{ background: 'rgba(220,240,245,0.05)', border: `1px solid ${LINE}`, padding: '10px 12px', borderRadius: 8, fontSize: 13, color: C.muted, marginBottom: 16, fontFamily: 'Inter' }}>
              <div><strong style={{ color: C.icy }}>Name:</strong> {quickSend.lead.first_name}</div>
              <div><strong style={{ color: C.icy }}>Business:</strong> {quickSend.lead.business_name}</div>
              <div><strong style={{ color: C.icy }}>Email:</strong> <span style={{ fontFamily: 'monospace' }}>{quickSend.lead.email}</span></div>
              {quickSend.lead.phone && <div><strong style={{ color: C.icy }}>Phone:</strong> {quickSend.lead.phone}</div>}
              <div style={{ marginTop: 6, fontSize: 12, color: '#5FE0BE' }}>On successful send, stage <strong>new → contacted</strong> (undo-able).</div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button data-testid="lead-quicksend-cancel" onClick={() => setQuickSend(null)} style={ghostBtn}>Cancel</button>
              <button data-testid="lead-quicksend-confirm" onClick={() => confirmQuickSend()} disabled={busy} style={btn(MAGENTA)}>
                {busy ? 'Sending…' : `✉ Send to ${quickSend.lead.first_name}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div data-testid="leads-toast" style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: SURFACE, border: `1px solid ${LINE}`, color: C.icy, padding: '12px 24px', borderRadius: 999, fontSize: 13, boxShadow: '0 12px 40px rgba(0,0,0,0.5)', zIndex: 200, fontFamily: 'Inter' }}>
          {toast}
        </div>
      )}

      {/* DELETED LEADS — restore drawer */}
      {showDeleted && (
        <div data-testid="deleted-leads-drawer" onClick={() => setShowDeleted(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(16,8,56,0.78)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 150, padding: 24, overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} className="studio-scroll"
            style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 16, maxWidth: 720, width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: '24px 28px', marginTop: 60 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 10.5, letterSpacing: '.22em', textTransform: 'uppercase', color: AMBER, fontWeight: 700, fontFamily: 'Inter', margin: 0 }}>Tombstones · restorable</p>
                <h2 style={{ margin: '4px 0 0', fontFamily: 'Poppins,Inter,sans-serif', fontSize: 22, color: C.icy }}>Deleted leads</h2>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: C.faint, fontFamily: 'Inter' }}>
                  Restoring removes the tombstone <b style={{ color: TEAL }}>and pins the lead</b> so the next backfill sweep won&apos;t re-suppress it. If the source contact still exists (contact form, discover session…), the lead reappears within 30 minutes.
                </p>
              </div>
              <button data-testid="deleted-drawer-close" onClick={() => setShowDeleted(false)}
                style={{ ...ghostBtn, padding: '4px 10px', fontSize: 12 }}>✕ Close</button>
            </div>
            {tombstones === null ? (
              <p style={{ color: C.muted, fontFamily: 'Inter', textAlign: 'center', padding: 30 }}>Loading…</p>
            ) : tombstones.length === 0 ? (
              <div style={{ padding: '30px 20px', textAlign: 'center', border: `1px dashed ${LINE}`, borderRadius: 12 }}>
                <p style={{ fontSize: 22, margin: 0 }}>🎉</p>
                <p style={{ margin: '6px 0 0', color: C.muted, fontFamily: 'Inter', fontSize: 13 }}>Nothing deleted. Clean slate.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tombstones.map((t) => {
                  const name = [t.first_name, t.business_name].filter(Boolean).join(' · ') || '—';
                  const when = t.deleted_at ? new Date(t.deleted_at).toLocaleString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                  const isBusy = restoring === t.lead_id;
                  return (
                    <div key={t.lead_id} data-testid={`tombstone-row-${t.lead_id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${LINE}`, borderRadius: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 13, fontWeight: 700, color: C.icy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</p>
                        <p style={{ margin: '2px 0 0', fontFamily: 'monospace', fontSize: 11, color: C.faint }}>{t.email || '(no email)'}</p>
                        {when && <p style={{ margin: '2px 0 0', fontFamily: 'Inter', fontSize: 10.5, color: C.faint }}>deleted {when}</p>}
                      </div>
                      <button data-testid={`tombstone-restore-${t.lead_id}`}
                        disabled={isBusy}
                        onClick={() => restoreTombstone(t)}
                        style={{ ...btn(TEAL, C.navy), padding: '6px 14px', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: isBusy ? 0.55 : 1 }}>
                        <Undo2 size={13} /> {isBusy ? 'Restoring…' : 'Restore'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Portal-rendered stage menu — fixes the recurring "menu behind
          tile" stacking-context bug. document.body + zIndex 5000. */}
      {stageMenu && (() => {
        const lead = leads.find(l => l.lead_id === stageMenu.leadId);
        if (!lead) return null;
        return (
          <PortalStageMenu
            leadId={stageMenu.leadId}
            rect={stageMenu.rect}
            currentStage={lead.stage}
            onPick={(newStage) => { setStageMenu(null); changeStage(lead, newStage); }}
            onClose={() => setStageMenu(null)}
          />
        );
      })()}
    </Chrome>
  );
}
